/**
 * Support Reply Renderer
 *
 * @module SupportReplyRenderer
 *
 * Custom renderer for "support-reply" notifications. Shows the reply preview
 * and a link that opens the support widget to that conversation (via the
 * `?support=<conversationId>` deep-link that AppShell consumes).
 */

import React from 'react';
import { Lifebuoy } from '@phosphor-icons/react';
import { useRouter } from '@tanstack/react-router';
import { registerNotificationRenderer } from '../NotificationRegistry';
import type { NotificationRendererProps } from '../NotificationRegistry';

function SupportReplyRenderer({ notification }: NotificationRendererProps) {
  const router = useRouter();
  const { conversationId, preview } = notification.data as {
    conversationId?: string;
    preview?: string;
  };

  const openConversation = () => {
    if (!conversationId) return;
    router.navigate({
      to: '/',
      search: (prev: Record<string, unknown>) => ({ ...prev, support: conversationId }),
    });
  };

  return (
    <div className="flex items-start gap-2">
      <Lifebuoy size={18} weight="fill" className="mt-0.5 shrink-0 text-blue-500" />
      <div className="min-w-0">
        <p className="text-sm text-neutral-900 dark:text-neutral-100">{notification.title}</p>
        {preview && (
          <p className="mt-0.5 line-clamp-2 text-xs text-neutral-500 dark:text-neutral-400">
            {preview}
          </p>
        )}
        {conversationId && (
          <button
            type="button"
            onClick={openConversation}
            className="mt-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400"
          >
            View conversation
          </button>
        )}
      </div>
    </div>
  );
}

registerNotificationRenderer('support-reply', SupportReplyRenderer);
